import React, { useCallback, useEffect, useMemo, useRef } from "react";

import { groupPeopleByRole } from "./teamViewsHelpers";
import TeamViewsPersonCard, {
  type PersonCardVariant,
} from "./TeamViewsPersonCard";
import TeamViewsPersonRow from "./TeamViewsPersonRow";
import type { TeamViewsPerson } from "../../../Store/Reducers/TeamViewsSlice";

type TeamViewsRoleSectionsProps = {
  people: TeamViewsPerson[];
  /** Currently highlighted role section (from chip or scroll-spy). */
  activeRole: string;
  /** Bumps when a role chip is clicked — triggers magnetic scroll once. */
  scrollRequestId: number;
  /** Dedicated content pane — magnetic scroll stays inside this element. */
  scrollRootRef: React.RefObject<HTMLElement | null>;
  onActiveRoleChange: (role: string) => void;
  /** Cards grid vs accordion list. */
  layout?: "cards" | "list";
  cardVariant?: PersonCardVariant;
  showPortfolioHighlight?: boolean;
  /** When set, matching people get a green "You" chip. */
  youUserId?: string | null;
  /** Privileged viewers only (VA / C-Level / ESL / executiveOps). */
  canManageTeamCoverage?: boolean;
  onSelectPerson?: (person: TeamViewsPerson) => void;
  onReassignPerson?: (person: TeamViewsPerson) => void;
  onReassignAccount?: (person: TeamViewsPerson, accountId: string) => void;
  onHistoryAccount?: (person: TeamViewsPerson, accountId: string) => void;
};

const getSectionScrollTop = (
  root: HTMLElement,
  section: HTMLElement
): number => {
  const rootRect = root.getBoundingClientRect();
  const sectionRect = section.getBoundingClientRect();
  return sectionRect.top - rootRect.top + root.scrollTop;
};

/** Keep section headers a few px under the scroll pane top when jumping. */
const SCROLL_TOP_PADDING = 8;

/**
 * Role-grouped roster with Guidance Library–style magnetic scroll
 * (cards grid or list accordion) inside the dedicated content pane.
 *
 * Scroll-spy only highlights role chips — never "All". "All" is selected
 * only via the chip strip (which exits this grouped view for a flat A–Z list).
 */
const TeamViewsRoleSections: React.FC<TeamViewsRoleSectionsProps> = ({
  people,
  activeRole,
  scrollRequestId,
  scrollRootRef,
  onActiveRoleChange,
  layout = "cards",
  cardVariant = "v3",
  showPortfolioHighlight = false,
  youUserId = null,
  canManageTeamCoverage = false,
  onSelectPerson,
  onReassignPerson,
  onReassignAccount,
  onHistoryAccount,
}) => {
  const sections = useMemo(() => groupPeopleByRole(people), [people]);
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});
  const scrollingToRef = useRef(false);
  const scrollEndTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scrollRafRef = useRef<number | null>(null);
  const activeRoleRef = useRef(activeRole);
  const pendingRoleRef = useRef<string | null>(null);
  const lastScrollRequestId = useRef(0);
  const scrollListenersCleanup = useRef<(() => void) | null>(null);

  useEffect(() => {
    activeRoleRef.current = activeRole;
  }, [activeRole]);

  useEffect(
    () => () => {
      if (scrollEndTimer.current) clearTimeout(scrollEndTimer.current);
      if (scrollRafRef.current !== null) {
        cancelAnimationFrame(scrollRafRef.current);
      }
      scrollListenersCleanup.current?.();
    },
    []
  );

  const resolveActiveFromScroll = useCallback(() => {
    const root = scrollRootRef.current;
    if (!root || !sections.length) return null;

    const scrollAnchor = root.scrollTop + SCROLL_TOP_PADDING + 4;
    let current = sections[0].roleLabel;

    for (const { roleLabel } of sections) {
      const el = sectionRefs.current[roleLabel];
      if (!el) continue;
      if (getSectionScrollTop(root, el) <= scrollAnchor + 2) {
        current = roleLabel;
      } else {
        break;
      }
    }
    return current;
  }, [sections, scrollRootRef]);

  const finishScroll = useCallback(() => {
    scrollingToRef.current = false;
    scrollListenersCleanup.current?.();
    scrollListenersCleanup.current = null;
    if (pendingRoleRef.current) {
      onActiveRoleChange(pendingRoleRef.current);
      pendingRoleRef.current = null;
      return;
    }
    const resolved = resolveActiveFromScroll();
    if (resolved) onActiveRoleChange(resolved);
  }, [onActiveRoleChange, resolveActiveFromScroll]);

  const scrollToRole = useCallback(
    (roleLabel: string, behavior: ScrollBehavior = "smooth") => {
      const root = scrollRootRef.current;
      const el = sectionRefs.current[roleLabel];
      if (!root || !el) return false;

      scrollListenersCleanup.current?.();
      if (scrollEndTimer.current) clearTimeout(scrollEndTimer.current);

      scrollingToRef.current = true;
      pendingRoleRef.current = roleLabel;
      // Highlight immediately (Guidance Library / GroupedBrowse pattern).
      onActiveRoleChange(roleLabel);

      const sectionTop = getSectionScrollTop(root, el);
      root.scrollTo({
        top: Math.max(0, sectionTop - SCROLL_TOP_PADDING),
        behavior,
      });

      if (behavior === "auto") {
        scrollingToRef.current = false;
        pendingRoleRef.current = null;
        return true;
      }

      const onScrollEnd = () => {
        if (scrollEndTimer.current) clearTimeout(scrollEndTimer.current);
        finishScroll();
      };

      const onScroll = () => {
        if (scrollEndTimer.current) clearTimeout(scrollEndTimer.current);
        scrollEndTimer.current = setTimeout(finishScroll, 120);
      };

      const supportsScrollEnd = "onscrollend" in window;
      if (supportsScrollEnd) {
        root.addEventListener("scrollend", onScrollEnd, { once: true });
        scrollListenersCleanup.current = () => {
          root.removeEventListener("scrollend", onScrollEnd);
        };
        scrollEndTimer.current = setTimeout(() => {
          root.removeEventListener("scrollend", onScrollEnd);
          finishScroll();
        }, 900);
      } else {
        root.addEventListener("scroll", onScroll, { passive: true });
        scrollListenersCleanup.current = () => {
          root.removeEventListener("scroll", onScroll);
        };
        scrollEndTimer.current = setTimeout(() => {
          root.removeEventListener("scroll", onScroll);
          finishScroll();
        }, 900);
      }
      return true;
    },
    [finishScroll, onActiveRoleChange, scrollRootRef]
  );

  // Chip click → wait for layout (double rAF), retry once if the section ref
  // is not ready yet (avoids the remount/layout lag).
  useEffect(() => {
    if (!scrollRequestId || scrollRequestId === lastScrollRequestId.current) {
      return undefined;
    }
    lastScrollRequestId.current = scrollRequestId;
    if (!activeRole || activeRole === "all") return undefined;
    if (!sections.some(s => s.roleLabel === activeRole)) return undefined;

    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    const attempt = (triesLeft: number) => {
      if (cancelled) return;
      const ok = scrollToRole(activeRole, "smooth");
      if (!ok && triesLeft > 0) {
        retryTimer = setTimeout(() => attempt(triesLeft - 1), 50);
      }
    };

    const outer = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => attempt(3));
    });

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(outer);
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [scrollRequestId, activeRole, sections, scrollToRole]);

  useEffect(() => {
    const root = scrollRootRef.current;
    if (!root || !sections.length) return undefined;

    const onScroll = () => {
      if (scrollingToRef.current) return;
      if (scrollRafRef.current !== null) {
        cancelAnimationFrame(scrollRafRef.current);
      }
      scrollRafRef.current = requestAnimationFrame(() => {
        scrollRafRef.current = null;
        const resolved = resolveActiveFromScroll();
        if (resolved && resolved !== activeRoleRef.current) {
          onActiveRoleChange(resolved);
        }
      });
    };

    root.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      root.removeEventListener("scroll", onScroll);
      if (scrollRafRef.current !== null) {
        cancelAnimationFrame(scrollRafRef.current);
      }
    };
  }, [sections, resolveActiveFromScroll, onActiveRoleChange, scrollRootRef]);

  if (!sections.length) {
    return <p className="text-sm text-gray-500">No people in the roster.</p>;
  }

  return (
    <div className="tv-role-sections space-y-5">
      {sections.map(({ roleLabel, members }) => (
        <section
          key={roleLabel}
          id={`tv-role-${roleLabel.replace(/\s+/g, "-").toLowerCase()}`}
          ref={el => {
            sectionRefs.current[roleLabel] = el;
          }}
          className={`tv-role-section${
            activeRole === roleLabel ? " is-active" : ""
          }`}
          data-role={roleLabel}
        >
          <h4 className="tv-role-section-hdr">
            {roleLabel}
            <span className="tv-role-section-count">{members.length}</span>
          </h4>
          {layout === "list" ? (
            <div className="tv-accordion">
              {members.map(person => (
                <TeamViewsPersonRow
                  key={person.user_id}
                  person={person}
                  showPortfolioHighlight={showPortfolioHighlight}
                  isYou={
                    Boolean(youUserId) && String(person.user_id) === youUserId
                  }
                  canManageTeamCoverage={canManageTeamCoverage}
                  onReassignPerson={
                    canManageTeamCoverage ? onReassignPerson : undefined
                  }
                  onReassignAccount={
                    canManageTeamCoverage ? onReassignAccount : undefined
                  }
                  onHistoryAccount={onHistoryAccount}
                />
              ))}
            </div>
          ) : (
            <div className={`tv-cards-grid tv-cards-grid--${cardVariant}`}>
              {members.map(person => (
                <TeamViewsPersonCard
                  key={person.user_id}
                  person={person}
                  showPortfolioHighlight={showPortfolioHighlight}
                  variant={cardVariant}
                  isYou={
                    Boolean(youUserId) && String(person.user_id) === youUserId
                  }
                  showLastLogin={canManageTeamCoverage}
                  onClick={onSelectPerson}
                />
              ))}
            </div>
          )}
        </section>
      ))}
    </div>
  );
};

export default TeamViewsRoleSections;
